/** Enum-Werte – identisch mit den Prisma-Enums in apps/api/prisma/schema.prisma. */
const tuple = <T extends string[]>(...v: T) => v;

export const CHANNEL_TYPES = tuple('TEXT', 'VOICE', 'CATEGORY', 'ANNOUNCEMENT', 'FORUM', 'DM', 'GROUP_DM', 'PUBLIC_THREAD', 'PRIVATE_THREAD');
export type ChannelType = (typeof CHANNEL_TYPES)[number];
/** Kanäle, die zu einem Server gehören und in der Kanalliste erscheinen. */
export const SERVER_CHANNEL_TYPES: readonly ChannelType[] = ['TEXT', 'VOICE', 'CATEGORY', 'ANNOUNCEMENT', 'FORUM'];
export const TEXT_BASED_CHANNEL_TYPES: readonly ChannelType[] = ['TEXT', 'ANNOUNCEMENT', 'DM', 'GROUP_DM', 'PUBLIC_THREAD', 'PRIVATE_THREAD', 'VOICE'];
export const THREAD_CHANNEL_TYPES: readonly ChannelType[] = ['PUBLIC_THREAD', 'PRIVATE_THREAD'];
export const DM_CHANNEL_TYPES: readonly ChannelType[] = ['DM', 'GROUP_DM'];
export const isThread = (t: ChannelType): boolean => t === 'PUBLIC_THREAD' || t === 'PRIVATE_THREAD';
export const isDm = (t: ChannelType): boolean => t === 'DM' || t === 'GROUP_DM';
/** Sprachkanäle haben zusätzlich einen Text-Chat (wie Discord „Text in Voice"). */
export const isTextBased = (t: ChannelType): boolean => TEXT_BASED_CHANNEL_TYPES.includes(t);

export const MESSAGE_TYPES = tuple(
  'DEFAULT', 'REPLY', 'SYSTEM', 'THREAD_CREATED', 'THREAD_STARTER', 'MEMBER_JOIN', 'PIN_ADDED',
  'CHANNEL_NAME_CHANGE', 'RECIPIENT_ADD', 'RECIPIENT_REMOVE', 'CALL',
);
export type MessageType = (typeof MESSAGE_TYPES)[number];

/** Nachrichten-Flags (Bitfeld, Int). */
export const MessageFlags = {
  SUPPRESS_EMBEDS: 1 << 0,
  /** Keine Push-/Ton-Benachrichtigung („@silent"). */
  SILENT: 1 << 1,
  /** Aus Discord gespiegelt. */
  BRIDGED: 1 << 2,
  /** Von einer geplanten Nachricht erzeugt. */
  SCHEDULED: 1 << 3,
  /** Beitrag kam über einen eingehenden Webhook. */
  WEBHOOK: 1 << 4,
} as const;

export const PRESENCE_STATUSES = tuple('ONLINE', 'IDLE', 'DND', 'INVISIBLE', 'OFFLINE');
export type PresenceStatus = (typeof PRESENCE_STATUSES)[number];

export const OVERWRITE_TARGETS = tuple('ROLE', 'MEMBER');
export type OverwriteTarget = (typeof OVERWRITE_TARGETS)[number];

export const NOTIFICATION_LEVELS = tuple('DEFAULT', 'ALL', 'MENTIONS', 'NOTHING');
export type NotificationLevel = (typeof NOTIFICATION_LEVELS)[number];

export const NOTIFICATION_TYPES = tuple('MENTION', 'REPLY', 'DM', 'THREAD_REPLY', 'REMINDER', 'SYSTEM', 'EXPORT_READY', 'SCHEDULED_FAILED');
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export const THEMES = tuple('SYSTEM', 'DARK', 'LIGHT', 'CUSTOM');
export type Theme = (typeof THEMES)[number];
export const MESSAGE_DISPLAYS = tuple('COZY', 'COMPACT');
export type MessageDisplay = (typeof MESSAGE_DISPLAYS)[number];
export const DM_POLICIES = tuple('EVERYONE', 'SERVER_MEMBERS', 'NOBODY');
export type DmPolicy = (typeof DM_POLICIES)[number];

export const SCHEDULED_STATUSES = tuple('PENDING', 'SENT', 'FAILED', 'CANCELED');
export type ScheduledStatus = (typeof SCHEDULED_STATUSES)[number];

export const EXPORT_FORMATS = tuple('JSON', 'HTML', 'MARKDOWN');
export type ExportFormat = (typeof EXPORT_FORMATS)[number];
export const JOB_STATUSES = tuple('PENDING', 'RUNNING', 'DONE', 'FAILED', 'EXPIRED');
export type JobStatus = (typeof JOB_STATUSES)[number];

export const EMBED_TYPES = tuple('LINK', 'RICH', 'IMAGE', 'VIDEO', 'GIFV');
export type EmbedType = (typeof EMBED_TYPES)[number];

export const STICKER_FORMATS = tuple('PNG', 'APNG', 'GIF', 'WEBP', 'LOTTIE');
export type StickerFormat = (typeof STICKER_FORMATS)[number];

export const BRIDGE_GUILD_STATUSES = tuple('PENDING', 'IMPORTING', 'ACTIVE', 'PAUSED', 'ERROR');
export type BridgeGuildStatus = (typeof BRIDGE_GUILD_STATUSES)[number];
export const BRIDGE_CHANNEL_MODES = tuple('READONLY', 'BIDIRECTIONAL', 'PAUSED');
export type BridgeChannelMode = (typeof BRIDGE_CHANNEL_MODES)[number];
export const BRIDGE_ORIGINS = tuple('DISCORD', 'PLATFORM');
export type BridgeOrigin = (typeof BRIDGE_ORIGINS)[number];

export const DELIVERY_STATUSES = tuple('PENDING', 'SUCCESS', 'FAILED');
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/** Scopes für API-Tokens (Bot-/Plugin-API). `*` = alles, was der Besitzer darf. */
export const API_SCOPES = tuple(
  '*', 'messages.read', 'messages.write', 'channels.read', 'channels.write', 'servers.read', 'servers.write',
  'members.read', 'members.write', 'reactions.write', 'webhooks.manage', 'users.read', 'gateway',
);
export type ApiScope = (typeof API_SCOPES)[number];

/** Ereignisse für ausgehende Webhooks (Teilmenge des Event-Katalogs). */
export const OUTGOING_WEBHOOK_EVENTS = tuple(
  'MESSAGE_CREATE', 'MESSAGE_UPDATE', 'MESSAGE_DELETE', 'MESSAGE_REACTION_ADD', 'MESSAGE_REACTION_REMOVE',
  'SERVER_MEMBER_ADD', 'SERVER_MEMBER_REMOVE', 'CHANNEL_CREATE', 'CHANNEL_UPDATE', 'CHANNEL_DELETE', 'THREAD_CREATE',
);
export type OutgoingWebhookEvent = (typeof OUTGOING_WEBHOOK_EVENTS)[number];

export const AUDIT_ACTIONS = tuple(
  // Server
  'server.update', 'channel.create', 'channel.update', 'channel.delete', 'overwrite.update', 'overwrite.delete',
  'role.create', 'role.update', 'role.delete', 'member.update', 'member.roles', 'member.kick', 'member.ban', 'member.unban',
  'member.timeout', 'invite.create', 'invite.delete', 'emoji.create', 'emoji.update', 'emoji.delete',
  'sticker.create', 'sticker.update', 'sticker.delete', 'sound.create', 'sound.update', 'sound.delete',
  'webhook.create', 'webhook.update', 'webhook.delete', 'message.delete', 'message.bulk_delete', 'message.pin', 'message.unpin',
  'thread.update', 'thread.delete', 'voice.move', 'voice.disconnect', 'voice.server_mute',
  // Plattform
  'admin.user.update', 'admin.user.disable', 'admin.user.enable', 'admin.user.delete', 'admin.user.password_reset',
  'admin.server.delete', 'admin.limits.update', 'admin.bridge.update', 'auth.login', 'auth.login_failed',
  'bot.create', 'bot.delete', 'token.create', 'token.revoke',
);
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
